/**
 * SensorPlacementPage.jsx
 *
 * The unique-differentiator-unblocker. Lets users map their physical buried
 * Ecowitt probes (WH51 moisture, WN34 temp) to agronomic roles (DRY/AVG/WET)
 * and the corresponding Rachio sprinkler zone. Without this mapping, TurfPulse
 * Guardian stays in advisory-only mode. Once all 3 WH51 + all 3 WN34 channels
 * are assigned, the Guardian flips to auto_fire (probe-driven MAD).
 *
 * Routes off `/sensors/placement`. Backend at `/api/sensors/placement`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Helmet } from 'react-helmet-async';
import { API_URL } from '../config/api';
import { ArrowLeft, CheckCircle2, AlertTriangle, Droplets, Thermometer } from 'lucide-react';
import ZoneOverridesCard from '../components/ZoneOverridesCard';

const ROLES = [
  { value: 'DRY', label: 'DRY — Dries first / irrigation trigger', desc: 'Full sun, high spot, or most actively transpiring turf' },
  { value: 'AVG', label: 'AVG — Representative average', desc: 'Behaves like the bulk of the lawn' },
  { value: 'WET', label: 'WET — Holds water longest', desc: 'Shaded, low corner, or over-water guard' },
];

const CHANNELS = ['1', '2', '3'];

const blankMap = () => ({
  '1': { role: '', sprinkler_zone_number: '', note: '' },
  '2': { role: '', sprinkler_zone_number: '', note: '' },
  '3': { role: '', sprinkler_zone_number: '', note: '' },
});

const SensorPlacementPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [wh51, setWh51] = useState(blankMap());
  const [wn34, setWn34] = useState(blankMap());
  const [live, setLive] = useState({ wh51: {}, wn34: {} });
  const [guardianMode, setGuardianMode] = useState('advisory_only');
  const [confirmedAt, setConfirmedAt] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [ecowittConnected, setEcowittConnected] = useState(false);

  const fetchPlacement = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/api/sensors/placement`, { withCredentials: true });
      const placements = data.placements || {};
      const fromBackend = (m) => ({
        '1': m?.['1'] || { role: '', sprinkler_zone_number: '', note: '' },
        '2': m?.['2'] || { role: '', sprinkler_zone_number: '', note: '' },
        '3': m?.['3'] || { role: '', sprinkler_zone_number: '', note: '' },
      });
      setWh51(fromBackend(placements.wh51));
      setWn34(fromBackend(placements.wn34));
      setLive(data.live || { wh51: {}, wn34: {} });
      setGuardianMode(data.guardian_mode || 'advisory_only');
      setConfirmedAt(data.confirmed_at || null);
      setConfirmed(!!data.sensors_placement_confirmed);
      setEcowittConnected(!!data.ecowitt_connected);
    } catch (e) {
      toast.error('Could not load sensor placement. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlacement();
  }, []);

  const validate = (sens) => {
    const filled = Object.values(sens).filter((c) => c.role);
    if (filled.length === 0) return null; // empty allowed
    if (filled.length < 3) return 'Assign all 3 channels (DRY, AVG, WET) — partial save will not unlock auto-fire.';
    const roles = filled.map((c) => c.role);
    const unique = new Set(roles);
    if (unique.size !== 3) return 'Each channel must be a different role: DRY, AVG, and WET (one of each).';
    return null;
  };

  const wh51Error = useMemo(() => validate(wh51), [wh51]);
  const wn34Error = useMemo(() => validate(wn34), [wn34]);

  const fullyMapped =
    !wh51Error && !wn34Error &&
    Object.values(wh51).every((c) => c.role) &&
    Object.values(wn34).every((c) => c.role);

  const updateChannel = (kind, ch, field, value) => {
    const setter = kind === 'wh51' ? setWh51 : setWn34;
    setter((prev) => ({ ...prev, [ch]: { ...prev[ch], [field]: value } }));
  };

  const handleSave = async () => {
    if (wh51Error || wn34Error) {
      toast.error(wh51Error || wn34Error);
      return;
    }
    try {
      setSaving(true);
      const cleanMap = (m) => Object.fromEntries(
        Object.entries(m).filter(([, c]) => c.role).map(([ch, c]) => [
          ch,
          {
            role: c.role,
            sprinkler_zone_number: c.sprinkler_zone_number ? Number(c.sprinkler_zone_number) : null,
            note: c.note || '',
          },
        ])
      );
      const body = { wh51: cleanMap(wh51), wn34: cleanMap(wn34) };
      const { data } = await axios.post(`${API_URL}/api/sensors/placement`, body, { withCredentials: true });
      if (data.sensors_placement_confirmed) {
        toast.success('🎯 Probe-driven Guardian unlocked — MAD now reads from your buried probes.');
      } else {
        toast.success('Mapping saved. Assign all 3 channels per sensor type to flip Guardian to auto-fire.');
      }
      await fetchPlacement();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Save failed. Please try again.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Drop probe → zone mapping and return Guardian to advisory mode?')) return;
    try {
      setResetting(true);
      await axios.post(`${API_URL}/api/sensors/placement/reset`, {}, { withCredentials: true });
      toast.success('Mapping reset. Guardian is back in advisory mode.');
      await fetchPlacement();
    } catch (e) {
      toast.error('Reset failed. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  const guardianBadge = () => {
    if (guardianMode === 'auto_fire') {
      return (
        <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="guardian-mode-badge">
          <CheckCircle2 className="w-3 h-3 mr-1" /> Probe-driven · auto-fire
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" data-testid="guardian-mode-badge">
        <AlertTriangle className="w-3 h-3 mr-1" /> Advisory only
      </Badge>
    );
  };

  const renderChannelRow = (kind, ch, sens, liveData) => {
    const liveVal =
      kind === 'wh51'
        ? liveData?.moisture_pct != null
          ? `${Math.round(liveData.moisture_pct)}%`
          : '—'
        : liveData?.temp_f != null
          ? `${liveData.temp_f.toFixed(1)}°F`
          : '—';
    const battery = liveData?.battery_level;

    return (
      <div
        key={`${kind}-${ch}`}
        className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end p-3 border rounded-md bg-card"
        data-testid={`placement-row-${kind}-${ch}`}
      >
        <div className="md:col-span-1 flex md:block items-center gap-2">
          <div className="text-xs text-muted-foreground">Channel</div>
          <div className="font-mono font-semibold text-lg">CH{ch}</div>
        </div>
        <div className="md:col-span-2">
          <div className="text-xs text-muted-foreground">Live reading</div>
          <div className="font-medium" data-testid={`placement-live-${kind}-${ch}`}>{liveVal}</div>
          {battery != null && (
            <div className="text-[10px] text-muted-foreground">batt {battery}</div>
          )}
        </div>
        <div className="md:col-span-4">
          <Label className="text-xs">Role</Label>
          <Select
            value={sens[ch].role || ''}
            onValueChange={(v) => updateChannel(kind, ch, 'role', v)}
          >
            <SelectTrigger data-testid={`placement-role-${kind}-${ch}`}>
              <SelectValue placeholder="Pick a role" />
            </SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Rachio zone #</Label>
          <Input
            type="number" min="1" max="32"
            value={sens[ch].sprinkler_zone_number || ''}
            onChange={(e) => updateChannel(kind, ch, 'sprinkler_zone_number', e.target.value)}
            placeholder="e.g. 2"
            data-testid={`placement-zone-${kind}-${ch}`}
          />
        </div>
        <div className="md:col-span-3">
          <Label className="text-xs">Placement note (optional)</Label>
          <Input
            value={sens[ch].note || ''}
            onChange={(e) => updateChannel(kind, ch, 'note', e.target.value)}
            placeholder="Front trophy, full sun"
            data-testid={`placement-note-${kind}-${ch}`}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Sensor Placement — TurfPulse</title>
        <meta name="description" content="Map your buried Ecowitt probes to lawn zones. Unlock probe-driven Guardian mode." />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <main className="container mx-auto max-w-5xl px-4 py-8" data-testid="sensor-placement-page">
        <div className="mb-6">
          <Button variant="ghost" size="sm" asChild data-testid="placement-back-button">
            <Link to="/dashboard"><ArrowLeft className="w-4 h-4 mr-1" /> Back to dashboard</Link>
          </Button>
        </div>

        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Sensor Placement</h1>
            <p className="text-muted-foreground mt-1 max-w-2xl">
              Tell TurfPulse where each buried probe physically sits. Once all 3 moisture (WH51) and 3 temperature (WN34) channels are mapped, the Guardian unlocks <span className="font-semibold">probe-driven MAD</span> — your lawn's irrigation decisions stop guessing.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {guardianBadge()}
            {confirmedAt && (
              <div className="text-xs text-muted-foreground" data-testid="placement-confirmed-at">
                Confirmed {new Date(confirmedAt).toLocaleString()}
              </div>
            )}
          </div>
        </div>

        {!ecowittConnected && (
          <Alert variant="destructive" className="mb-6" data-testid="placement-no-ecowitt">
            <AlertTriangle className="w-4 h-4" />
            <AlertTitle>Ecowitt not connected</AlertTitle>
            <AlertDescription>
              Connect your Ecowitt API keys on <Link to="/profile" className="underline">your profile</Link> first.
              Without live readings, probe placement can't be validated.
            </AlertDescription>
          </Alert>
        )}

        {confirmed && (
          <Alert className="mb-6 border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40" data-testid="placement-confirmed-banner">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <AlertTitle>Probe-driven Guardian active</AlertTitle>
            <AlertDescription>
              MAD bucket and irrigation decisions now read from your buried probes.
              No commercial controller in the homeowner price range works this way.
            </AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-12 text-muted-foreground" data-testid="placement-loading">Loading placement…</div>
        ) : (
          <>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Droplets className="w-5 h-5 text-sky-600" />
                  Soil Moisture (WH51)
                </CardTitle>
                <CardDescription>
                  Three buried 4" probes. Map each to its agronomic role and the Rachio zone it sits within.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {CHANNELS.map((ch) => renderChannelRow('wh51', ch, wh51, live.wh51?.[ch]))}
                {wh51Error && (
                  <div className="text-sm text-destructive" data-testid="placement-wh51-error">{wh51Error}</div>
                )}
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Thermometer className="w-5 h-5 text-amber-600" />
                  Soil Temperature (WN34)
                </CardTitle>
                <CardDescription>
                  Same 3 spots, soil-temp probes. Match them to the same zones as your moisture probes for consistent reads.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {CHANNELS.map((ch) => renderChannelRow('wn34', ch, wn34, live.wn34?.[ch]))}
                {wn34Error && (
                  <div className="text-sm text-destructive" data-testid="placement-wn34-error">{wn34Error}</div>
                )}
              </CardContent>
            </Card>

            {/* Phase B — Per-Zone Overrides (planters / ornamentals / isolated beds) */}
            <div className="mb-6">
              <ZoneOverridesCard />
            </div>

            <div className="sticky bottom-2 z-10 bg-background/95 backdrop-blur border rounded-lg p-3 flex items-center justify-between gap-3 shadow-sm">
              <div className="text-xs md:text-sm text-muted-foreground">
                {fullyMapped
                  ? '✅ Fully mapped — saving will flip Guardian to probe-driven auto-fire.'
                  : 'Partial save allowed. Full triad (DRY+AVG+WET on both sensor types) unlocks auto-fire.'}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  disabled={resetting}
                  data-testid="placement-reset-button"
                >
                  Reset
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving || !!wh51Error || !!wn34Error}
                  data-testid="placement-save-button"
                >
                  {saving ? 'Saving…' : 'Save Placement'}
                </Button>
              </div>
            </div>
          </>
        )}
      </main>
    </>
  );
};

export default SensorPlacementPage;
